import { useTheme } from 'styled-components';

import { Tooltip, Typography } from '@speeki/global-ui-components';

import { TaskTooltipContent } from '@components/molecules/StatisticsCards/components/TaskTooltipContent/TaskTooltipContent';

import { TaskCardBaseFragment } from '@graphql/generated/graphql';

import { ChartTextWrapper, TaskChart } from './TasksCard.styled';

export interface TaskStatistic {
    completed: {
        totalCount: number;
    };
    inProgress: {
        overdueCount?: number;
        totalCount: number;
        upcomingCount?: number;
    };
    notStarted: {
        overdueCount?: number;
        totalCount: number;
        upcomingCount?: number;
    };
    totalCount: number;
}

interface TasksCardChartProps {
    allTasks: TaskCardBaseFragment[];
    statistics: TaskStatistic;
    totalTasks: number;
}

const SVG_SIZE = 140;
const STROKE_WIDTH = 10;
const RADIUS = 65;

export const TasksCardChart = ({
    allTasks,
    statistics,
    totalTasks,
}: TasksCardChartProps) => {
    const { palette } = useTheme();

    const circumference = 2 * Math.PI * RADIUS;

    const completedCount = statistics.completed.totalCount || 0;
    const inProgressCount = statistics.inProgress.totalCount || 0;
    const notStartedCount = statistics.notStarted.totalCount || 0;

    const total = totalTasks > 0 ? totalTasks : 1;

    const notStartedPct = notStartedCount / total;
    const inProgressPct = inProgressCount / total;
    const completedPct = completedCount / total;

    const inProgressOffset = completedPct;
    const notStartedOffset = completedPct + inProgressPct;

    const completedTasks = allTasks.filter((task) => task.status === 'CLOSED');
    const inProgressTasks = allTasks.filter(
        (task) => task.status === 'IN_PROGRESS',
    );
    const notStartedTasks = allTasks.filter((task) => task.status === 'OPEN');

    const renderCircle = (
        color: string,
        offset: number,
        transformOffset: number,
        tasks: TaskCardBaseFragment[],
        title: string,
        count: number,
    ) => (
        <Tooltip
            jsxText={
                <TaskTooltipContent count={count} tasks={tasks} title={title} />
            }
            placement="top"
            testId="_qn-mi2"
        >
            <circle
                cx={SVG_SIZE / 2}
                cy={SVG_SIZE / 2}
                fill="none"
                r={RADIUS}
                stroke={color}
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap={'butt'}
                strokeWidth={STROKE_WIDTH}
                style={{ cursor: 'pointer' }}
                transform={`rotate(${transformOffset} 70 70)`}
            />
        </Tooltip>
    );

    return (
        <TaskChart>
            <svg
                height={SVG_SIZE}
                viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
                width={SVG_SIZE}
            >
                <circle
                    cx={SVG_SIZE / 2}
                    cy={SVG_SIZE / 2}
                    fill="none"
                    r={RADIUS}
                    stroke={palette.neutral.gray}
                    strokeWidth={STROKE_WIDTH}
                />

                {notStartedCount > 0 &&
                    renderCircle(
                        palette.neutral.gray,
                        circumference * (1 - notStartedPct),
                        360 * notStartedOffset - 90,
                        notStartedTasks,
                        'Not Started',
                        notStartedCount,
                    )}

                {inProgressCount > 0 &&
                    renderCircle(
                        palette.chart.blue500,
                        circumference * (1 - inProgressPct),
                        360 * inProgressOffset - 90,
                        inProgressTasks,
                        'In Progress',
                        inProgressCount,
                    )}

                {completedCount > 0 &&
                    renderCircle(
                        palette.semantic.success,
                        circumference * (1 - completedPct),
                        -90,
                        completedTasks,
                        'Completed',
                        completedCount,
                    )}
            </svg>

            <ChartTextWrapper>
                <Typography.TextH2_28_44 fontWeight="regular">
                    {totalTasks}
                </Typography.TextH2_28_44>
                <Typography.TextBody_10_16>
                    {totalTasks === 1 ? 'task' : 'tasks'}
                </Typography.TextBody_10_16>
            </ChartTextWrapper>
        </TaskChart>
    );
};
